/**
 * App theme switching. Themes are pure CSS-token overrides keyed off the
 * `data-theme` attribute on <html> (see the `[data-theme=...]` blocks in
 * index.css). "Taupe" is the default (the bare `:root` tokens — no attribute).
 *
 * The choice persists in localStorage (`xavior:theme`) and is applied before
 * first paint by an inline script in index.html; this module keeps React in
 * sync and lets any component read/set the current theme via `useTheme`.
 */
import { useCallback, useEffect, useState } from 'react';

export type ThemeId = 'taupe' | 'dark' | 'infinite-loop' | 'nordic' | 'atelier' | 'swiss';

export interface ThemeOption {
  id: ThemeId;
  label: string;
  hint: string;
  /** [desk/rail, panel] swatch colors for the picker preview — the theme's two
   *  actual background tones (no accent stripe). */
  swatch: [string, string];
  /** Registered but not offered in the theme picker (the theme's CSS and
   *  ThemeId stay live, so anyone with it persisted keeps it working). */
  hidden?: boolean;
}

export const THEME_OPTIONS: ThemeOption[] = [
  { id: 'nordic', label: 'Nordic', hint: 'Pine, paper & soft daylight (default)', swatch: ['#e6d3ab', '#fbf8f1'] },
  { id: 'atelier', label: 'Atelier', hint: 'Walnut, parchment & quiet focus', swatch: ['#5e3620', '#e9e6df'] },
  { id: 'infinite-loop', label: 'Infinite Loop', hint: 'Crisp glass & clean light', swatch: ['#f5f5f7', '#ffffff'] },
  { id: 'taupe', label: 'Clay', hint: 'Warm neutral & calm focus', swatch: ['#e4e0d3', '#fafaf6'] },
  { id: 'dark', label: 'Obsidian', hint: 'Low-light, quiet & refined', swatch: ['#0d0d0f', '#1a1a1d'] },
  // Hidden from the picker for now (not deleted — CSS + ThemeId stay live).
  { id: 'swiss', label: 'Swiss', hint: 'Hairlines, one accent & tabular grids', swatch: ['#ff0000', '#ffffff'], hidden: true },
];

const STORAGE_KEY = 'xavior:theme';
const CHANGE_EVENT = 'xavior:theme-changed';

function isThemeId(v: unknown): v is ThemeId {
  return v === 'taupe' || v === 'dark' || v === 'infinite-loop' || v === 'nordic' || v === 'atelier' || v === 'swiss';
}

export function getStoredTheme(): ThemeId {
  try {
    const t = localStorage.getItem(STORAGE_KEY);
    if (isThemeId(t)) return t;
  } catch { /* SSR / blocked storage */ }
  return 'nordic';
}

/** Apply a theme to <html>, persist it, and notify listeners. */
export function applyTheme(theme: ThemeId): void {
  if (typeof document !== 'undefined') {
    const el = document.documentElement;
    // Taupe is the bare :root default — no attribute keeps the CSS simplest.
    if (theme === 'taupe') el.removeAttribute('data-theme');
    else el.setAttribute('data-theme', theme);
  }
  try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: theme }));
  }
}

/** Read + set the current theme; stays in sync across components/tabs. */
export function useTheme(): [ThemeId, (theme: ThemeId) => void] {
  const [theme, setThemeState] = useState<ThemeId>(getStoredTheme);

  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (isThemeId(detail)) setThemeState(detail);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && isThemeId(e.newValue)) setThemeState(e.newValue);
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setTheme = useCallback((next: ThemeId) => applyTheme(next), []);
  return [theme, setTheme];
}
